import React from "react";
import { useTranslation } from "react-i18next";
import { Link, NavLink, useNavigate } from "react-router-dom";
import logo from "src/assets/images/clearpath-logo.png";
import Button from "src/components/Buttons/Button";
import { ButtonColors } from "src/components/Buttons/buttons.types";
import CloseIcon from "src/components/Icons/CloseIcon";
import ImageV2 from "src/components/Images/ImageV2";
import { useAppDispatch } from "src/store";
import { getSectionsWithPages } from "../config/docs-config";
import useDocsSidebar from "../slices/hooks/use-docs-sidebar";
import { closeDocsSidebar } from "../slices/docs-sidebar-slice";
import { MdArrowBack } from "react-icons/md";

/**
 * Sidebar navigation component for documentation.
 * Renders sections and pages in a hierarchical structure.
 * Toggleable similar to NavigationMenu.
 */
function DocsSidebar() {
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { isVisible } = useDocsSidebar();
  const sectionsWithPages = getSectionsWithPages();

  const handleBackToDashboard = () => navigate("/dashboard");
  const handleCloseSidebar = () => dispatch(closeDocsSidebar());

  return (
    <nav
      id="docsSidebar"
      className={
        isVisible
          ? "leftNavContainer leftNavContainerShown"
          : "leftNavContainer leftNavContainerHidden"
      }
    >
      <div className="leftNav">
        {/* Logo */}
        <div className="logoContainer">
          <Link
            onClick={handleCloseSidebar}
            to="/dashboard"
            style={{ width: "80%" }}
          >
            <ImageV2
              loading="lazy"
              src={logo}
              alt="clearpath logo"
              height={40}
              width={112}
            />
          </Link>
          <Button
            className="closeButton"
            onClick={handleCloseSidebar}
            title={undefined}
          >
            <CloseIcon />
          </Button>
        </div>

        {/* Navigation Links */}
        <div className="navLinksContainer">
          <ul className="navLinksList">
            {sectionsWithPages.map(({ section, pages }) => (
              <React.Fragment key={section.id}>
                {/* Section Header */}
                <li className="navLinkListItem px-20 py-5">
                  <span className="text-sm font-semibold text-cp-yellow uppercase tracking-wide">
                    {section.title}
                  </span>
                </li>
                {/* Section Pages */}
                {pages.map((page) => {
                  const to = `/docs/${page.sectionId}/${page.slug}`;
                  return (
                    <li key={page.id} className="navLinkListItem">
                      <NavLink
                        className={({ isActive }) =>
                          isActive ? "navLinkSelected" : "navLink"
                        }
                        onClick={handleCloseSidebar}
                        to={to}
                      >
                        <span className="navLinkText">{page.title}</span>
                      </NavLink>
                    </li>
                  );
                })}
              </React.Fragment>
            ))}
          </ul>
        </div>

        {/* Back to Dashboard Button */}
        <div className="flex justify-center p-20">
          <Button color={ButtonColors.Gray} onClick={handleBackToDashboard}>
            <MdArrowBack size={20} />
            {t("backToDashboard")}
          </Button>
        </div>
      </div>
    </nav>
  );
}

export default React.memo(DocsSidebar);
